/**
 * MetricWeeklyBars — Barras verticales por día de la semana para tarjetas KPI.
 *
 * QUÉ HACE: pinta 7 columnas iguales con la etiqueta superior (p. ej. "18tx"), la
 *   barra proporcional al mayor valor y el día de la semana al pie. Con
 *   `showAxis` añade un eje Y de 3 guías punteadas (máximo, medio y 0) en un
 *   gutter izquierdo, alineado con el carril de las barras. La usan las tarjetas
 *   "Ticket Promedio" y "Ventas Web".
 * QUÉ INVOCA: `useAnimatedSeries` (subida/bajada de las alturas).
 * QUÉ EXPORTA: MetricWeeklyBars, MetricWeeklyBar.
 * QUÉ NO DEBE HACER: no calcula métricas ni accede a datos.
 */

import { useAnimatedSeries } from "@/hooks/useAnimatedSeries";
import { MOTION } from "@/lib/animation/motion";

/** Columna de la gráfica semanal. */
export interface MetricWeeklyBar {
  /** Etiqueta del día ("Lun", "Mar", "Hoy"). */
  label: string;
  /** Valor crudo: define la altura relativa de la barra. */
  value: number;
  /** Etiqueta superior ("18tx"). */
  valueLabel: string;
}

interface MetricWeeklyBarsProps {
  items: MetricWeeklyBar[];
  /** Clases del relleno de la barra (color). */
  fillClassName?: string;
  /** Dibuja el eje Y de 3 guías punteadas (máximo, medio y 0). */
  showAxis?: boolean;
  className?: string;
}

export const MetricWeeklyBars = ({
  items,
  fillClassName = "bg-indigo-600",
  showAxis = false,
  className = "",
}: MetricWeeklyBarsProps) => {
  const animatedItems = useAnimatedSeries(items, {
    keys: ["value"],
    duration: MOTION.barRise,
    stagger: MOTION.barRise,
  });
  const max = items.reduce((top, item) => Math.max(top, item.value), 0);
  const gutter = showAxis ? "pl-7 sm:pl-8" : "";

  return (
    <div className={`flex min-h-0 flex-1 flex-col ${className}`}>
      <div className={`flex items-end justify-between gap-1 ${gutter}`}>
        {items.map((item, index) => (
          <span
            key={`value-${item.label}-${index}`}
            className="min-w-0 flex-1 truncate text-center text-[6px] font-semibold text-slate-500 sm:text-[7px] dark:text-slate-400"
          >
            {item.valueLabel}
          </span>
        ))}
      </div>

      <div
        className={`relative mt-0.5 flex min-h-[2.25rem] flex-1 items-end justify-between gap-1 sm:min-h-[3rem] ${gutter}`}
      >
        {showAxis && (
          <>
            <div className="pointer-events-none absolute inset-y-0 left-0 w-7 text-right text-[7px] font-normal tabular-nums text-slate-400 sm:w-8 sm:text-[8px] dark:text-slate-500">
              <span className="absolute right-1.5 top-0">{max}</span>
              <span className="absolute right-1.5 top-1/2 -translate-y-1/2">
                {Math.round(max / 2)}
              </span>
              <span className="absolute right-1.5 bottom-0 translate-y-1/2">
                0
              </span>
            </div>
            <div className="pointer-events-none absolute inset-y-0 left-7 right-0 z-10 sm:left-8">
              <div className="absolute inset-x-0 top-0 border-t border-dashed border-slate-200 dark:border-slate-700" />
              <div className="absolute inset-x-0 top-1/2 border-t border-dashed border-slate-200 dark:border-slate-700" />
              <div className="absolute inset-x-0 bottom-0 border-t border-dashed border-slate-200 dark:border-slate-700" />
            </div>
          </>
        )}

        {items.map((item, index) => (
          <div
            key={`bar-${item.label}-${index}`}
            title={`${item.label} · ${item.valueLabel}`}
            className="flex h-full min-w-0 flex-1 items-end overflow-hidden rounded-t-sm bg-slate-100 dark:bg-slate-700/40"
          >
            <div
              className={`w-full rounded-t-sm transition-colors duration-300 ${fillClassName}`}
              style={{
                height: `${
                  max > 0
                    ? Math.max(
                        6,
                        Math.round(
                          ((animatedItems[index]?.value ?? item.value) / max) *
                            100,
                        ),
                      )
                    : 0
                }%`,
              }}
            />
          </div>
        ))}
      </div>

      <div className={`mt-0.5 flex justify-between gap-1 ${gutter}`}>
        {items.map((item, index) => (
          <span
            key={`label-${item.label}-${index}`}
            className="min-w-0 flex-1 truncate text-center text-[6px] font-medium text-slate-600 sm:text-[7px] dark:text-slate-300"
          >
            {item.label}
          </span>
        ))}
      </div>
    </div>
  );
};
